import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconExternalLink, IconPlus, IconSearch, IconSparkles, IconTrash, IconUsers, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import {
  fetchCampaign,
  patchCampaign,
  patchPhase,
  patchTouch,
  createTouch,
  deleteTouch,
  deleteCampaign,
  regenerateCampaignDrafts,
  generateCampaignMessages,
  generateTouchPage,
  fetchPageAnalytics,
  discoverProspects,
  importProspects,
} from '../services/api';

const STATUS_OPTIONS = ['draft', 'active', 'paused', 'archived'];

function TouchRow({
  campaignId, campaignChannel, touch, touchNumber, onPatched, onDeleted, canDelete,
  locked, hostedPages, pageGenerating, onGeneratePage,
}) {
  const api = useApi();
  const timerRef = useRef(null);
  const [sendDayOffset, setSendDayOffset] = useState(touch.sendDayOffset ?? 7);
  const [messageTemplate, setMessageTemplate] = useState(touch.messageTemplate || '');
  const [smsTemplate, setSmsTemplate] = useState(touch.smsTemplate || '');
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState('');

  const isDualChannel = campaignChannel === 'whatsapp';
  const linkedPage = (hostedPages || []).find((p) => p.url === touch.destinationUrl) || null;
  // URL provided in the brief (not one of our hosted pages): just display it, no generate option
  const isExternalUrl = Boolean(touch.destinationUrl) && !linkedPage && !touch.hosted;
  const pageOptions = (hostedPages || []).map((p) => ({
    value: p.url,
    label: p.status === 'draft' ? `${p.title || p.slug} (draft)` : (p.title || p.slug),
  }));

  async function handleLinkPage(url) {
    try {
      const updated = await patchTouch(api, campaignId, touch.id, {
        destinationUrl: url || '',
        hosted: Boolean(url),
      });
      onPatched(updated);
    } catch (err) {
      notifications.show({
        title: 'Failed to link page',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  useEffect(() => {
    setSendDayOffset(touch.sendDayOffset ?? 7);
    setMessageTemplate(touch.messageTemplate || '');
    setSmsTemplate(touch.smsTemplate || '');
  }, [touch.id, touch.sendDayOffset, touch.messageTemplate, touch.smsTemplate]);

  function queueSave(nextPatch) {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('Saving...');
    timerRef.current = setTimeout(async () => {
      try {
        const updated = await patchTouch(api, campaignId, touch.id, nextPatch);
        onPatched(updated);
        setStatus('Saved');
        setTimeout(() => setStatus(''), 1000);
      } catch (err) {
        setStatus('Save failed');
        notifications.show({
          title: 'Failed to save touch',
          message: err.response?.data?.error || 'Something went wrong',
          color: 'red',
        });
      }
    }, 800);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteTouch(api, campaignId, touch.id);
      onDeleted(touch.id);
    } catch (err) {
      notifications.show({
        title: 'Failed to delete touch',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
      setDeleting(false);
    }
  }

  return (
    <Grid align="flex-start" gutter="sm">
      <Grid.Col span={6}>
        <Group gap={6} mb={4}>
          <Text fw={600} size="sm">Touch {touchNumber}</Text>
          <Badge size="xs" variant="light" color="blue">
            {isDualChannel ? 'WhatsApp/SMS' : campaignChannel}
          </Badge>
          {touch.title && <Text size="xs" fw={500} c="dimmed">— {touch.title}</Text>}
        </Group>
        {touch.thesis && (
          <Text size="xs" c="dimmed" mb={4}>{touch.thesis}</Text>
        )}
        <Textarea
          label={isDualChannel ? 'WhatsApp message' : 'Message'}
          value={messageTemplate}
          placeholder={locked ? 'Writing this message...' : undefined}
          minRows={2}
          autosize
          disabled={locked}
          onChange={(e) => {
            const next = e.currentTarget.value;
            setMessageTemplate(next);
            queueSave({ sendDayOffset, messageTemplate: next, smsTemplate });
          }}
        />
        {isDualChannel && (
          <Textarea
            label="SMS message"
            mt="xs"
            value={smsTemplate}
            placeholder={locked ? 'Writing this message...' : 'Shorter SMS version...'}
            minRows={2}
            autosize
            disabled={locked}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setSmsTemplate(next);
              queueSave({ sendDayOffset, messageTemplate, smsTemplate: next });
            }}
          />
        )}
        {locked ? (
          <Group gap={6} mt={4}>
            <Loader size={12} color="violet" />
            <Text size="xs" c="violet">Generating this message...</Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed" mt={4}>{status}</Text>
        )}
      </Grid.Col>
      <Grid.Col span={2}>
        <NumberInput
          label="Days after previous"
          min={0}
          value={sendDayOffset}
          disabled={locked}
          onChange={(value) => {
            const next = typeof value === 'number' ? value : 0;
            setSendDayOffset(next);
            queueSave({ sendDayOffset: next, messageTemplate, smsTemplate });
          }}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <Text size="sm" fw={500} mb={4}>Landing page</Text>
        {isExternalUrl ? (
          <>
            <Group gap={4} mb={2} wrap="nowrap">
              <Anchor size="xs" href={touch.destinationUrl} target="_blank" rel="noopener noreferrer" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Group gap={2} wrap="nowrap">
                  <IconExternalLink size={12} />
                  {touch.destinationUrl.replace(/^https?:\/\//, '')}
                </Group>
              </Anchor>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                title="Remove this URL"
                disabled={locked || pageGenerating}
                onClick={() => handleLinkPage('')}
              >
                <IconX size={12} />
              </ActionIcon>
            </Group>
            <Text size="xs" c="dimmed">Destination from your brief.</Text>
          </>
        ) : (
          <>
            {touch.destinationUrl ? (
              <Group gap={4} mb={6} wrap="nowrap">
                <Anchor size="xs" href={touch.destinationUrl} target="_blank" rel="noopener noreferrer" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Group gap={2} wrap="nowrap">
                    <IconExternalLink size={12} />
                    {linkedPage ? (linkedPage.title || linkedPage.slug) : touch.destinationUrl.replace(/^https?:\/\//, '')}
                  </Group>
                </Anchor>
                {linkedPage?.status === 'draft' && (
                  <Badge size="xs" variant="light" color="yellow">Draft</Badge>
                )}
              </Group>
            ) : (
              <Text size="xs" c="dimmed" mb={6}>No page linked yet.</Text>
            )}
            <Stack gap={6}>
              <Select
                size="xs"
                placeholder="Link existing page..."
                data={pageOptions}
                value={linkedPage ? linkedPage.url : null}
                searchable
                clearable
                disabled={locked || pageGenerating}
                onChange={handleLinkPage}
              />
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconSparkles size={12} />}
                loading={pageGenerating}
                disabled={locked}
                onClick={() => onGeneratePage(touch)}
              >
                {touch.destinationUrl
                  ? 'Regenerate page'
                  : `Generate ${touch.pageKind || 'page'}`}
              </Button>
              {pageGenerating && (
                <Text size="xs" c="violet">Writing this page — a few minutes...</Text>
              )}
            </Stack>
          </>
        )}
      </Grid.Col>
      <Grid.Col span={1} style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 28 }}>
        <ActionIcon
          color="red"
          variant="subtle"
          onClick={handleDelete}
          loading={deleting}
          disabled={!canDelete || locked}
          title="Delete touch"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Grid.Col>
    </Grid>
  );
}

const SOURCE_OPTIONS = [
  { value: 'places', label: 'Local businesses (Google Maps)' },
  { value: 'apollo', label: 'People search (Apollo)' },
];

function ProspectsPanel({ campaign, onCampaignUpdate }) {
  const api = useApi();
  const targeting = campaign.targeting || {};
  const [source, setSource] = useState(targeting.source === 'apollo' ? 'apollo' : 'places');
  const [location, setLocation] = useState(targeting.location || '');
  const [businessCategory, setBusinessCategory] = useState(targeting.businessCategory || '');
  const [titles, setTitles] = useState((targeting.titles || []).join(', '));
  const [keywords, setKeywords] = useState((targeting.keywords || []).join(', '));
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [discovering, setDiscovering] = useState(false);
  const [importing, setImporting] = useState(false);

  function toList(text) {
    return text.split(',').map((s) => s.trim()).filter(Boolean);
  }

  async function handleDiscover() {
    setDiscovering(true);
    setCandidates(null);
    try {
      const nextTargeting = {
        source,
        location: location.trim() || null,
        businessCategory: businessCategory.trim() || null,
        titles: toList(titles),
        industries: targeting.industries || [],
        keywords: toList(keywords),
      };
      const result = await discoverProspects(api, campaign.id, nextTargeting);
      setCandidates(result.candidates);
      setSelected(new Set(result.candidates.filter((c) => !c.existing).map((c) => c.externalId)));
      onCampaignUpdate({ targeting: nextTargeting });
      if (!result.candidates.length) {
        notifications.show({ title: 'No prospects found', message: 'Try broadening the location or keywords.', color: 'yellow' });
      }
    } catch (err) {
      notifications.show({
        title: 'Prospect search failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setDiscovering(false);
    }
  }

  async function handleImport() {
    const chosen = (candidates || []).filter((c) => selected.has(c.externalId));
    if (!chosen.length) return;
    setImporting(true);
    try {
      const { imported, skipped } = await importProspects(api, campaign.id, chosen);
      notifications.show({
        title: 'Prospects imported',
        message: `${imported} lead${imported === 1 ? '' : 's'} added to this campaign${skipped ? ` (${skipped} skipped as duplicates)` : ''}. Outreach drafts are being prepared.`,
        color: 'teal',
      });
      setCandidates((prev) => (prev || []).map((c) => (selected.has(c.externalId) ? { ...c, existing: true } : c)));
      setSelected(new Set());
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setImporting(false);
    }
  }

  function toggleSelected(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Paper withBorder p="md">
      <Group gap={8} mb="sm">
        <IconUsers size={18} />
        <Text fw={600}>Prospects</Text>
        <Badge size="xs" variant="light" color="violet">Beta</Badge>
      </Group>
      <Grid align="flex-end" gutter="sm">
        <Grid.Col span={3}>
          <Select label="Source" data={SOURCE_OPTIONS} value={source} onChange={(v) => setSource(v || 'places')} />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput label="Location" placeholder="e.g. Morwell VIC, Australia" value={location} onChange={(e) => setLocation(e.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={4}>
          {source === 'places' ? (
            <TextInput label="Business type" placeholder="e.g. security system installers" value={businessCategory} onChange={(e) => setBusinessCategory(e.currentTarget.value)} />
          ) : (
            <TextInput label="Job titles (comma-separated)" placeholder="e.g. founder, practice manager" value={titles} onChange={(e) => setTitles(e.currentTarget.value)} />
          )}
        </Grid.Col>
        <Grid.Col span={2}>
          <Button
            fullWidth
            leftSection={<IconSearch size={14} />}
            loading={discovering}
            onClick={handleDiscover}
          >
            Find prospects
          </Button>
        </Grid.Col>
      </Grid>
      {source === 'apollo' && (
        <TextInput
          mt="xs"
          label="Keywords (comma-separated)"
          placeholder="e.g. career coaching, leadership development"
          value={keywords}
          onChange={(e) => setKeywords(e.currentTarget.value)}
        />
      )}

      {discovering && (
        <Text size="xs" c="dimmed" mt="sm">Searching {source === 'apollo' ? 'Apollo' : 'Google Maps'}...</Text>
      )}

      {candidates && candidates.length > 0 && (
        <>
          <ScrollArea mt="md" h={Math.min(candidates.length * 44 + 48, 320)}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>Name</Table.Th>
                  <Table.Th>{source === 'apollo' ? 'Title / Company' : 'Contact'}</Table.Th>
                  <Table.Th>{source === 'apollo' ? 'Location' : 'Address'}</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {candidates.map((c) => (
                  <Table.Tr key={c.externalId} opacity={c.existing ? 0.5 : 1}>
                    <Table.Td>
                      <Checkbox
                        checked={selected.has(c.externalId)}
                        disabled={c.existing}
                        onChange={() => toggleSelected(c.externalId)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{c.name}</Text>
                      {c.website && <Anchor size="xs" href={c.website} target="_blank" rel="noopener noreferrer">{c.website.replace(/^https?:\/\//, '').slice(0, 40)}</Anchor>}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{source === 'apollo' ? [c.title, c.company].filter(Boolean).join(' — ') : (c.phone || c.email || '—')}</Text>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{c.address || '—'}</Text></Table.Td>
                    <Table.Td>
                      {c.existing
                        ? <Badge size="xs" variant="light" color="gray">Already a lead</Badge>
                        : (typeof c.rating === 'number' && c.rating > 0 ? <Text size="xs" c="dimmed">★ {c.rating}</Text> : null)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <Group justify="flex-end" mt="sm">
            <Button
              color="teal"
              leftSection={<IconUsers size={14} />}
              loading={importing}
              disabled={selected.size === 0}
              onClick={handleImport}
            >
              Import {selected.size} as leads
            </Button>
          </Group>
        </>
      )}
    </Paper>
  );
}

export default function CampaignBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const titleDebounce = useRef(null);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [phases, setPhases] = useState([]);
  const [touches, setTouches] = useState([]);
  const [titleDraft, setTitleDraft] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingMessages, setGeneratingMessages] = useState(false);
  const [hostedPages, setHostedPages] = useState([]);
  const [generatingPages, setGeneratingPages] = useState({});
  const [stylingModalOpen, setStylingModalOpen] = useState(false);
  const [stylingUrlDraft, setStylingUrlDraft] = useState('');
  const [pendingPageTouch, setPendingPageTouch] = useState(null);
  const autoGenTriggered = useRef(false);

  async function loadPages() {
    try {
      const data = await fetchPageAnalytics(api);
      setHostedPages(data.pages || []);
    } catch {
      // Page list is only used for the link-existing selector; non-fatal.
    }
  }

  async function loadCampaign() {
    setLoading(true);
    try {
      const data = await fetchCampaign(api, id);
      const nextCampaign = data.campaign || data;
      setCampaign(nextCampaign);
      setPhases(data.phases || []);
      setTouches(data.touches || []);
      setTitleDraft(nextCampaign.name || '');
    } catch (err) {
      notifications.show({
        title: 'Failed to load campaign',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCampaign();
    loadPages();
  }, [id]);

  useEffect(() => {
    if (!campaign) return;
    if (titleDraft === campaign.name) return;
    if (titleDebounce.current) clearTimeout(titleDebounce.current);
    titleDebounce.current = setTimeout(async () => {
      try {
        const updated = await patchCampaign(api, campaign.id, { name: titleDraft });
        setCampaign((prev) => ({ ...prev, ...(updated.campaign || updated) }));
      } catch (err) {
        notifications.show({
          title: 'Failed to update title',
          message: err.response?.data?.error || 'Something went wrong',
          color: 'red',
        });
      }
    }, 600);
  }, [titleDraft, campaign?.id]);

  const phaseMap = useMemo(() => {
    const map = new Map();
    phases.forEach((phase) => map.set(phase.id, phase));
    return map;
  }, [phases]);

  const activeTouches = touches.filter((touch) => phaseMap.get(touch.phaseId)?.active !== false);
  const activePhases = phases.filter((phase) => phase.active !== false);
  const avgSpacing = useMemo(() => {
    const offsets = activeTouches
      .map((touch) => Number(touch.sendDayOffset || 0))
      .sort((a, b) => a - b);
    if (offsets.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < offsets.length; i += 1) total += offsets[i] - offsets[i - 1];
    return Math.round(total / (offsets.length - 1));
  }, [activeTouches]);

  async function handleCampaignPatch(patch, options = { reload: false }) {
    if (!campaign) return;
    try {
      const updated = await patchCampaign(api, campaign.id, patch);
      setCampaign((prev) => ({ ...prev, ...(updated.campaign || updated) }));
      if (options.reload) await loadCampaign();
    } catch (err) {
      notifications.show({
        title: 'Failed to update campaign',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function handlePhasePatch(phaseId, patch) {
    try {
      await patchPhase(api, id, phaseId, patch);
      await loadCampaign();
    } catch (err) {
      notifications.show({
        title: 'Failed to update phase',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function handleAddTouch(phaseId) {
    try {
      const newTouch = await createTouch(api, campaign.id, { phaseId, sendDayOffset: 7 });
      setTouches((prev) => [...prev, newTouch]);
    } catch (err) {
      notifications.show({
        title: 'Failed to add touch',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function handleDeleteTouch(touchId) {
    setTouches((prev) => prev.filter((t) => t.id !== touchId));
  }

  // Generated campaigns get their touchpoint messages written automatically on
  // first arrival; "Regenerate messages" is the manual refresh. Landing pages
  // are always a per-touch decision (link existing or generate new).
  useEffect(() => {
    if (loading || !campaign || campaign.engine !== 'generated') return;
    if (autoGenTriggered.current || generatingMessages) return;
    if (!touches.length) return;
    const hasMessages = touches.some((t) => t.messageTemplate);
    if (hasMessages) return;
    autoGenTriggered.current = true;
    handleGenerateMessages();
  }, [loading, campaign, touches]);

  async function handleGenerateMessages() {
    if (!campaign) return;
    setGeneratingMessages(true);
    const startedAt = Date.now();
    console.log(`[generator] Writing touchpoint messages for campaign "${campaign.name}" (${campaign.channel} channel). One LLM call covers all touches — reasoning models can take several minutes...`);
    try {
      const { count } = await generateCampaignMessages(api, campaign.id);
      console.log(`[generator] Messages written for ${count} touches in ${((Date.now() - startedAt) / 1000).toFixed(1)}s`);
      notifications.show({
        title: 'Messages generated',
        message: `Proposed wording written for ${count} touch${count === 1 ? '' : 'es'}. Review and link a landing page to each.`,
        color: 'violet',
      });
      await loadCampaign();
    } catch (err) {
      console.error(`[generator] Message generation failed after ${((Date.now() - startedAt) / 1000).toFixed(1)}s:`, err.response?.data?.error || err.message, err);
      notifications.show({
        title: 'Failed to generate messages',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setGeneratingMessages(false);
    }
  }

  function handleGeneratePageRequest(touch) {
    // Styling reference is captured once per campaign, then reused.
    if (campaign?.stylingUrl || campaign?.brandKit) {
      runPageGeneration(touch.id, null);
      return;
    }
    setPendingPageTouch(touch);
    setStylingUrlDraft('');
    setStylingModalOpen(true);
  }

  async function runPageGeneration(touchId, stylingUrl) {
    setGeneratingPages((prev) => ({ ...prev, [touchId]: true }));
    const startedAt = Date.now();
    console.log(`[generator] Generating landing page for touch ${touchId}${stylingUrl ? ` (styling from ${stylingUrl})` : ' (using saved brand kit or default styling)'} — this can take a few minutes...`);
    try {
      const result = await generateTouchPage(api, campaign.id, touchId, stylingUrl);
      console.log(`[generator] Page "${result.page.title}" drafted in ${((Date.now() - startedAt) / 1000).toFixed(1)}s at ${result.touch.destinationUrl}`);
      setTouches((prev) => prev.map((t) => (t.id === result.touch.id ? result.touch : t)));
      if (result.stylingUrl) {
        setCampaign((prev) => ({ ...prev, stylingUrl: result.stylingUrl }));
      }
      notifications.show({
        title: result.warning ? 'Page generated (default styling)' : 'Page generated',
        message: result.warning || `"${result.page.title}" drafted. It publishes when the campaign is activated.`,
        color: result.warning ? 'yellow' : 'violet',
      });
      await loadPages();
    } catch (err) {
      console.error(`[generator] Page generation failed after ${((Date.now() - startedAt) / 1000).toFixed(1)}s:`, err.response?.data?.error || err.message, err);
      notifications.show({
        title: 'Failed to generate page',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setGeneratingPages((prev) => {
        const next = { ...prev };
        delete next[touchId];
        return next;
      });
    }
  }

  function handleStylingSubmit(e) {
    e.preventDefault();
    if (!pendingPageTouch) return;
    const touchId = pendingPageTouch.id;
    const url = stylingUrlDraft.trim();
    setStylingModalOpen(false);
    setPendingPageTouch(null);
    runPageGeneration(touchId, url || null);
  }

  function handleStylingSkip() {
    if (!pendingPageTouch) return;
    const touchId = pendingPageTouch.id;
    setStylingModalOpen(false);
    setPendingPageTouch(null);
    runPageGeneration(touchId, null);
  }

  async function handleSaveCampaign() {
    if (!campaign) return;
    try {
      const updated = await patchCampaign(api, campaign.id, {
        name: titleDraft,
        status: campaign.status,
      });
      setCampaign((prev) => ({ ...prev, ...(updated.campaign || updated) }));

      const { regenerated } = await regenerateCampaignDrafts(api, campaign.id);
      notifications.show({
        title: 'Campaign saved',
        message: regenerated > 0
          ? `Outreach messages regenerated for ${regenerated} prospect lead${regenerated !== 1 ? 's' : ''}.`
          : 'Changes saved. No prospect leads to update.',
        color: 'teal',
      });
    } catch (err) {
      notifications.show({
        title: 'Failed to save campaign',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  const campaignIsArchived = campaign?.status === 'archived' || campaign?.status === 'completed';

  async function handleDeleteCampaign() {
    if (!campaign) return;
    setDeleting(true);
    try {
      await deleteCampaign(api, campaign.id);
      notifications.show(campaignIsArchived
        ? { title: 'Campaign deleted', message: `"${campaign.name}" and its prospects have been permanently deleted.`, color: 'red' }
        : { title: 'Campaign archived', message: `"${campaign.name}" has been moved to Archived.`, color: 'gray' });
      navigate('/campaigns');
    } catch (err) {
      notifications.show({
        title: 'Failed to delete campaign',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  }

  if (loading || !campaign) {
    return (
      <Box p="xl">
        <Loader />
      </Box>
    );
  }

  return (
    <Box p="md">
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete campaign"
        centered
      >
        {campaignIsArchived ? (
          <Text size="sm" mb="md">
            <strong>{campaign.name}</strong> is archived. Deleting it now is <strong>permanent</strong>: the campaign, its phases and touches, and <strong>all prospects attached to it</strong> will be removed. This cannot be undone.
          </Text>
        ) : (
          <Text size="sm" mb="md">
            Delete <strong>{campaign.name}</strong>? The campaign and its phases, touches and history will be kept and moved to the <strong>archived</strong> state. Any scheduled tasks will be cancelled.
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button color="red" loading={deleting} onClick={handleDeleteCampaign}>
            {campaignIsArchived ? 'Delete permanently' : 'Delete & archive'}
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={stylingModalOpen}
        onClose={() => { setStylingModalOpen(false); setPendingPageTouch(null); }}
        title="Page styling"
        centered
      >
        <form onSubmit={handleStylingSubmit}>
          <Stack gap="sm">
            <Text size="sm">
              What should the new page look like? Enter your marketing website and the page will match its colours and fonts. This is asked once — later pages in this campaign reuse the same styling.
            </Text>
            <TextInput
              label="Styling website"
              placeholder="www.yourmarketingsite.com"
              value={stylingUrlDraft}
              onChange={(e) => setStylingUrlDraft(e.currentTarget.value)}
              data-autofocus
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={handleStylingSkip}>
                Skip — clean default styling
              </Button>
              <Button type="submit" color="violet" leftSection={<IconSparkles size={14} />}>
                Generate page
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Button variant="subtle" component={Link} to="/campaigns">Back to Campaigns</Button>
          <Group gap="xs">
            <TextInput
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.currentTarget.value)}
              styles={{ input: { fontSize: 22, fontWeight: 700, minWidth: 360 } }}
            />
            {campaign.engine === 'generated' && (
              <Badge variant="light" color="violet" leftSection={<IconSparkles size={12} />}>Generated</Badge>
            )}
          </Group>
          <Group gap="xs">
            <Select
              data={STATUS_OPTIONS}
              value={campaign.status || 'draft'}
              onChange={(value) => handleCampaignPatch({ status: value || 'draft' })}
              w={160}
              disabled={generatingMessages}
            />
            <Button color="teal" onClick={handleSaveCampaign} disabled={generatingMessages}>Save</Button>
            <Button color="red" variant="subtle" onClick={() => setDeleteModalOpen(true)} disabled={generatingMessages}>Delete</Button>
          </Group>
        </Group>

        {generatingMessages && (
          <Paper withBorder p="md">
            <Group gap="sm" mb={8}>
              <Loader size="sm" color="violet" />
              <Text fw={600} size="sm">Generating touchpoint messages</Text>
            </Group>
            <Progress value={100} animated color="violet" />
            <Text size="xs" c="dimmed" mt={6}>
              Writing the outreach message for every touch — typically 1–2 minutes. Editing is locked until it finishes.
            </Text>
          </Paper>
        )}

        {campaign.engine === 'generated' && (
          <Paper withBorder p="md" bg="var(--mantine-color-violet-0)">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Text fw={600} size="sm">Campaign brief</Text>
                <Text size="sm">{campaign.brief?.text || campaign.description}</Text>
                <Group gap="xs" mt={4}>
                  {campaign.brief?.archetype && <Badge size="xs" variant="outline" color="violet">{campaign.brief.archetype}</Badge>}
                  {campaign.brief?.endGoal && <Badge size="xs" variant="outline" color="gray">Goal: {campaign.brief.endGoal}</Badge>}
                </Group>
              </Stack>
              <Stack gap={4} align="flex-end">
                <Button
                  color="violet"
                  leftSection={<IconSparkles size={14} />}
                  loading={generatingMessages}
                  onClick={handleGenerateMessages}
                >
                  Regenerate messages
                </Button>
                {campaign.stylingUrl && (
                  <Text size="xs" c="dimmed">Page styling: {campaign.stylingUrl.replace(/^https?:\/\//, '')}</Text>
                )}
                {!generatingMessages && touches.some((t) => t.destinationUrl) && campaign.status !== 'active' && (
                  <Text size="xs" c="dimmed">Generated pages are drafts; activating the campaign publishes them.</Text>
                )}
              </Stack>
            </Group>
          </Paper>
        )}

        {campaign.engine === 'generated' && (
          <ProspectsPanel
            campaign={campaign}
            onCampaignUpdate={(patch) => setCampaign((prev) => ({ ...prev, ...patch }))}
          />
        )}

        <Paper withBorder p="md">
          <Group justify="space-between">
            <Stack gap={0}><Text fw={700} size="xl">{activeTouches.length}</Text><Text c="dimmed" size="sm">Active touches</Text></Stack>
            <Stack gap={0}><Text fw={700} size="xl">{activePhases.length}</Text><Text c="dimmed" size="sm">Active phases</Text></Stack>
            <Stack gap={0}><Text fw={700} size="xl">{avgSpacing}</Text><Text c="dimmed" size="sm">Avg spacing (days)</Text></Stack>
            <Stack gap={0}><Text fw={700} size="xl">{campaign.channel === 'whatsapp' ? 'WhatsApp/SMS' : (campaign.channel || 'email')}</Text><Text c="dimmed" size="sm">Channel</Text></Stack>
          </Group>
        </Paper>

        <Paper withBorder p="md" maw={360}>
          <Text fw={600} mb={8}>Audience type</Text>
          <SegmentedControl
            fullWidth
            data={[
              { label: 'Warm', value: 'warm' },
              { label: 'Cold', value: 'cold' },
            ]}
            value={campaign.audienceType || 'cold'}
            onChange={(value) => handleCampaignPatch({ audienceType: value }, { reload: true })}
          />
          <Text c="dimmed" size="xs" mt={8}>
            Warm collapses phases 2 and 3 to a light sequence.
          </Text>
        </Paper>

        <Paper withBorder p="sm">
          <Text size="xs" fw={600} mb={6}>Message variables</Text>
          <Group gap="xs" mb={4}>
            <Badge variant="outline" color="gray" size="sm">{'{{first_name}}'}</Badge>
            <Badge variant="outline" color="gray" size="sm">{'{{name}}'}</Badge>
            <Badge variant="outline" color="gray" size="sm">{'{{company}}'}</Badge>
          </Group>
          <Text size="xs" c="dimmed">Any URL in your message is automatically wrapped with click tracking.</Text>
        </Paper>

        {phases
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((phase, phaseIdx) => {
            const phaseTouches = touches
              .filter((touch) => touch.phaseId === phase.id)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            const inactive = phase.active === false;
            return (
              <Paper key={phase.id} withBorder p="md" style={{ opacity: inactive ? 0.5 : 1 }}>
                <Group justify="space-between" mb="sm">
                  <Group>
                    <Text fw={700}> {phaseIdx + 1}. {String(phase.kind || '').toUpperCase()} </Text>
                    <Text c="dimmed">{phaseTouches.length} touches</Text>
                    {inactive && <Badge color="gray" variant="light">Collapsed</Badge>}
                  </Group>
                  <Switch
                    checked={phase.active !== false}
                    disabled={generatingMessages}
                    onChange={(event) => handlePhasePatch(phase.id, { active: event.currentTarget.checked })}
                  />
                </Group>
                <Stack gap="sm">
                  {phaseTouches.map((touch, touchIdx) => (
                    <TouchRow
                      key={touch.id}
                      campaignId={campaign.id}
                      campaignChannel={campaign.channel || 'email'}
                      touch={touch}
                      touchNumber={touchIdx + 1}
                      canDelete={phaseTouches.length > 1}
                      locked={generatingMessages}
                      hostedPages={hostedPages}
                      pageGenerating={Boolean(generatingPages[touch.id])}
                      onGeneratePage={handleGeneratePageRequest}
                      onPatched={(updatedTouch) => {
                        setTouches((prev) => prev.map((item) => (item.id === updatedTouch.id ? updatedTouch : item)));
                      }}
                      onDeleted={handleDeleteTouch}
                    />
                  ))}
                  <Group justify="flex-start" mt="xs">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={12} />}
                      onClick={() => handleAddTouch(phase.id)}
                      disabled={generatingMessages}
                    >
                      Add Touch
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            );
          })}
      </Stack>
    </Box>
  );
}
